import React from "react";
import MonthlySummary from "./MonthlySummary";
import { enterpriseViewModel } from "common/viewModels";
import SummaryRow from "./SummaryRow";
import * as utils from "common/utils";
import { farmResultsViewModel } from "common/viewModels";
import Alert from "components/Alert";
import FileSummary from "./FileSummary";
import Watermark from "components/Watermark";

const getFarmArea = (budget) => {
    const productiveBlocks = budget.blocks.filter((b) => b.isProductive && b.type !== "FodderCrop");
    const productiveArea = productiveBlocks.reduce((total, block) => (total += isNaN(block.areaInHectares) ? 0 : block.areaInHectares), 0);
    const { totalFarmArea = 1 } = budget;
    return { productiveArea, totalFarmArea };
};

const getProductionSummary = (bud, ent) => {
    const { currentResults = {} } = bud;
    const { animalResults = {} } = currentResults;
    /*
    GHGAllocationToMilk: {name: "GHG: Allocation to milk", value: "0.50"}
    GHGAllocationToWoolBreeding: {name: "GHG: Allocation to wool - breeding mob", value: "0.12"}
    GHGAllocationToWoolTrading: {name: "GHG: Allocation to wool - trading mob", value: "1.00"}
    LiveweightBrought: {name: "Total liveweight brought (kg/ha grazed)", value: "38"}
    LiveweightReared: {name: "Total liveweight reared (kg/ha grazed)", value: "337"}
    LiveweightSold: {name: "Total liveweight sold (kg/ha grazed)", value: "977"}
    MilkProduction: {name: "Milk production per cow (kg milk solids / cow)", value: "120.0"}
    MilkSolids: {name: "Milk solids (kg/ha grazed)", value: "400"}
    MilkingHerdSize: {name: "Milking herd size (peak cows/ha grazed)", value: "3.3"}
    PercentageOfMaleBeefAnimals: {name: "Percent male beef animals", value: "3"}
    StockingRateBeef: {name: "Beef / dairy grazing stock rate (RSU)", value: "91"}
    StockingRateDairy: {name: "Dairy stock rate (RSU)", value: "663"}
    StockingRateDairyReplacements: {name: "Dairy replacements stock rate (RSU)", value: "0"}
    StockingRateSheep: {name: "Sheep stock rate (RSU)", value: "250"}
    WetlandAverageContribution: {name: "Wetlands: average contribution of N outside farm (%)", value: "-1602"}
    WetlandAverageEfficiency: {name: "Wetlands: average efficiency (%)", value: "45"}
    WetlandFarmRemovedLeaching: {name: "Wetlands: % farm leach N removed (%)", value: "2"}
    WetlandRemovedLeaching: {name: "Wetlands: % farm leach N thru wetland removed (%)", value: "11"}
    */
    const productionOtherValueKeys = ["LiveweightBrought", "LiveweightReared", "LiveweightSold", "MilkProduction", "MilkSolids", "MilkingHerdSize", "PercentageOfMaleBeefAnimals"];
    const farmProductionResults = farmResultsViewModel(bud).filter((r) => productionOtherValueKeys.includes(r.key)) || [];

    return ent.reduce(
        (totals, e) => {
            const animalResult = animalResults[e.id] || {};
            const entSum = {
                enterprise: e,
                total: 0,
            };
            totals.enterprises.push(entSum);
            entSum.total = utils.round(animalResult.total, 0);
            totals.total += entSum.total;
            return totals;
        },
        { total: 0, enterprises: [], farmProductionResults }
    );
};

const EnterpriseExtraSummary = ({ before, after, enterprises, baseEnterprises }) => {
    const actualTotals = getProductionSummary(after.details, enterprises);
    const baseTotals = before && baseEnterprises && getProductionSummary(before.details, baseEnterprises);

    const actualAreas = getFarmArea(after.details);
    const baseAreas = before && getFarmArea(before.details);

    const getGrazedAreaTotals = (budget, id) => {
        const { currentResults = {} } = budget;
        const { animalResults = {} } = currentResults;
        const result = animalResults[id];
        return result && result.grazedPerHa;
    };

    const allTypes = [...enterprises.map((e) => e.enterpriseTypeEnum), ...(before ? baseEnterprises.map((e) => e.enterpriseTypeEnum) : [])];
    const enterpriseTypes = Array.from(new Set(allTypes));

    if (enterpriseTypes.length === 0) return null;

    return (
        <div className="ReportSection">
            <h1>Enterprise RSU</h1>
            <div className="Table Table-Compressed">
                <Watermark />
                <table>
                    <thead>
                        <tr>
                            <th data-width="25"></th>
                            <th></th>
                            {before && (
                                <th className="u-textCenter u-before" data-width="12.5">
                                    {utils.truncateText(before.displayName, 35)}
                                </th>
                            )}
                            <th className={before ? "u-after u-textCenter" : "u-textCenter"} data-width="12.5">
                                {utils.truncateText(after.displayName, 35)}
                            </th>
                            {before && (
                                <th className="u-textCenter" data-width="16.7">
                                    Change
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {enterpriseTypes.map((type, i) => {
                            const actualValue = actualTotals.enterprises.find((b) => b.enterprise.enterpriseTypeEnum === type);
                            const baseValue = baseTotals && baseTotals.enterprises.find((b) => b.enterprise.enterpriseTypeEnum === type);
                            const name = (actualValue && actualValue.enterprise && actualValue.enterprise.enterpriseType) || (baseValue && baseValue.enterprise && baseValue.enterprise.enterpriseType);
                            const showTotalFarmArea = !before || (baseValue && baseAreas.totalFarmArea && actualAreas.totalFarmArea) ? true : false;
                            return (
                                <React.Fragment key={i}>
                                    <SummaryRow category={name} prefix={name} baseValue={before ? (baseValue ? baseValue.total : 0) : undefined} actualValue={actualValue ? actualValue.total : 0} label="Total RSU" unit="RSU" />
                                    {showTotalFarmArea && <SummaryRow category=" " prefix={name} baseValue={before ? (baseValue && baseAreas.totalFarmArea ? utils.round(baseValue.total / baseAreas.totalFarmArea, 2) : 0) : undefined} actualValue={actualValue && actualAreas.totalFarmArea ? utils.round(actualValue.total / actualAreas.totalFarmArea, 2) : 0} label="RSU per farm area" unit="RSU/ha" />}
                                    {false && <SummaryRow category=" " prefix={name} baseValue={before ? (baseValue ? utils.round(baseValue.total / baseAreas.productiveArea, 2) : 0) : undefined} actualValue={actualValue ? utils.round(actualValue.total / actualAreas.productiveArea, 2) : 0} label="RSU per productive area" unit="RSU/ha" />}
                                    <SummaryRow category=" " prefix={name} baseValue={before ? (baseValue ? utils.round(getGrazedAreaTotals(before.details, baseValue.enterprise.id), 2) : 0) : undefined} actualValue={actualValue ? utils.round(getGrazedAreaTotals(after.details, actualValue.enterprise.id), 2) : 0} label="RSU per grazed area" unit="RSU/ha" />
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export const EnterpriseSummary = ({ before, after, farm, refData, heading }) => {
    const enterprises = enterpriseViewModel(farm, after.details, refData);
    const baseEnterprises = before && enterpriseViewModel(farm, before.details, refData);

    const actualTotals = getProductionSummary(after.details, enterprises);
    const baseTotals = before && getProductionSummary(before.details, baseEnterprises);

    const actualAreas = getFarmArea(after.details);
    const baseAreas = before && getFarmArea(before.details);

    const uniqueProdKeys = Array.from(new Set([...actualTotals.farmProductionResults.map((r) => r.key), ...(baseTotals ? baseTotals.farmProductionResults.map((r) => r.key) : [])]));

    if (uniqueProdKeys.length === 0) return null;
    const showTotalFarmArea = !before || (baseTotals && baseAreas.totalFarmArea && actualAreas.totalFarmArea) ? true : false;

    return (
        <div className="ReportSection">
            <h1>Animals</h1>
            <div className="Table Table-Compressed">
                <Watermark />
                <table>
                    <thead>
                        <tr>
                            <th data-width="25"></th>
                            <th></th>
                            {before && (
                                <th className="u-textCenter u-before" data-width="12.5">
                                    {utils.truncateText(before.displayName, 35)}
                                </th>
                            )}
                            <th className={before ? "u-after u-textCenter" : "u-textCenter"} data-width="12.5">
                                {utils.truncateText(after.displayName, 35)}
                            </th>
                            {before && (
                                <th className="u-textCenter" data-width="16.7">
                                    Change
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        <SummaryRow baseValue={baseTotals && baseTotals.total} actualValue={actualTotals.total} category="RSU" prefix="RSU" label="Total RSU" unit="RSU" />

                        {showTotalFarmArea && <SummaryRow baseValue={baseTotals && baseAreas.totalFarmArea && utils.round(baseTotals.total / baseAreas.totalFarmArea, 2)} prefix="RSU" actualValue={actualAreas.totalFarmArea ? utils.round(actualTotals.total / actualAreas.totalFarmArea, 2) : 0} category=" " label="RSU per farm area" unit="RSU" />}
                        <SummaryRow baseValue={baseTotals && utils.round(baseTotals.total / baseAreas.productiveArea, 2)} prefix="RSU" actualValue={utils.round(actualTotals.total / actualAreas.productiveArea, 2)} category=" " label="RSU per productive area" unit="RSU" />
                        {uniqueProdKeys.map((key, i) => {
                            const actualValue = actualTotals.farmProductionResults.find((r) => r.key === key);
                            const baseValue = baseTotals && baseTotals.farmProductionResults.find((r) => r.key === key);
                            const aValue = actualValue || baseValue;
                            return <SummaryRow key={key} baseValue={before ? (baseValue && utils.round(baseValue.value, 2)) || 0 : undefined} actualValue={actualValue ? utils.round(actualValue.value, 2) : 0} category={i === 0 ? "Production" : " "} prefix="Production" label={aValue.name} />;
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const Animals = ({ before, after, refData, farm, showFileComparison = true }) => {
    const scenarios = [after];
    if (before) scenarios.unshift(before);

    const enterprises = enterpriseViewModel(farm, after.details, refData);
    const baseEnterprises = before && enterpriseViewModel(farm, before.details, refData);
    const allTypes = [...enterprises.map((e) => e.enterpriseTypeEnum), ...(before ? baseEnterprises.map((e) => e.enterpriseTypeEnum) : [])];

    const hasAnimalCount = () => {
        let result = false;
        scenarios.forEach((s) => {
            const { summary = {} } = s;
            const { categories = [] } = summary;
            const animalCountCat = categories.find((c) => c.name === "AnimalCount");
            if (animalCountCat && animalCountCat.rows && animalCountCat.rows.length > 0) {
                result = true;
            }
        });
        return result;
    };

    const showAnimalCount = hasAnimalCount();

    if (allTypes.length === 0) {
        return (
            <div className="u-pt-lg">
                <Alert text="The selected analysis does not contain animal information" type="Info" />
            </div>
        );
    }

    return (
        <div className="ReportPage">
            {showFileComparison && before && <FileSummary before={before} after={after} mini={true} />}
            {allTypes.length > 0 && (
                <>
                    <EnterpriseSummary before={before} after={after} farm={farm} refData={refData} />
                    <EnterpriseExtraSummary before={before} after={after} farm={farm} refData={refData} enterprises={enterprises} baseEnterprises={baseEnterprises} />
                </>
            )}
            {allTypes.length > 0 && (
                <>
                    <div className="ReportSection">
                        <h1>Enterprise RSU by Month</h1>
                        <MonthlySummary scenarios={scenarios} categoryName="RSU" rowName="Total" />
                    </div>
                    {showAnimalCount && (
                        <div className="ReportSection">
                            <h1>Enterprise Stock Numbers by Month</h1>
                            <MonthlySummary scenarios={scenarios} categoryName="AnimalCount" rowName="Total" disableSameY />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Animals;
